import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import api, { apiError } from "@/lib/api";
import { roleHome } from "@/lib/perms";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { PasswordField, StrengthMeter, passwordValid } from "@/components/shared/PasswordField";
import { useTranslation } from "react-i18next";

function AuthShell({ title, subtitle, children }) {
  const { t } = useContent();
  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-primary p-12 text-primary-foreground lg:flex">
        <Link to="/" className="flex items-center gap-2">
          <Logo alt="Logo" className="h-8 w-8 [filter:brightness(0)] dark:[filter:brightness(0)_invert(1)]" />
          <span className="font-display font-bold">{t("auth.brand", "The World Promotions")}</span>
        </Link>
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight">{t("auth.headline", "La promotion sociale, simplifiée.")}</h2>
          <p className="mt-4 max-w-md text-primary-foreground/70">{t("auth.tagline", "Livraison automatisée, prix transparents et suivi en temps réel. Rejoignez la plateforme dès aujourd'hui.")}</p>
        </div>
      </div>
      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-16">
        <Link to="/" className="mb-8 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> {t("auth.back_home", "Retour à l'accueil")}</Link>
        <div className="w-full max-w-sm">
          <h1 className="font-display text-2xl font-bold">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login, twofaLogin, logout } = useAuth();
  const { t } = useContent();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [code, setCode] = useState("");
  const [emailVerif, setEmailVerif] = useState(false);

  const go = (u) => {
    // Admin accounts must not authenticate through the customer login.
    if (u && ["admin", "super_admin"].includes(u.role)) {
      logout();
      setTempToken(null);
      setCode("");
      toast.error(t("auth.login.admin_blocked", "Ce compte n'est pas autorisé à se connecter ici."));
      return;
    }
    toast.success(t("auth.login.success", "Connexion réussie")); nav(roleHome(u));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res && res.requires_2fa) { setTempToken(res.temp_token); setEmailVerif(!!res.email_verification); toast.info(t("auth.twofa.required", "Code 2FA requis")); }
      else go(res);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("auth.login.error", "Échec de la connexion"));
    } finally { setLoading(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { go(await twofaLogin(tempToken, code)); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || t("auth.twofa.invalid", "Code invalide")); }
    finally { setLoading(false); }
  };

  if (tempToken) {
    return (
      <AuthShell title={emailVerif ? t("auth.agent_verif.title", "Vérification de votre adresse professionnelle") : t("auth.twofa.title", "Vérification 2FA")}
        subtitle={emailVerif ? t("auth.agent_verif.subtitle", "Première connexion : saisissez le code envoyé à votre adresse @theworldpromotions.com pour confirmer qu'elle vous appartient.") : t("auth.twofa.subtitle", "Entrez le code de vérification (application d'authentification ou code reçu par email selon votre compte).")}>
        <form onSubmit={submit2fa} className="mt-8 space-y-4">
          <Field label={t("auth.twofa.code_label", "Code à 6 chiffres (ou code de secours)")} value={code} onChange={setCode} testid="twofa-code" />
          <button disabled={loading} data-testid="twofa-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.twofa.submit", "Vérifier")}
          </button>
          <button type="button" onClick={() => { setTempToken(null); setCode(""); }} className="w-full text-sm text-muted-foreground hover:text-foreground">{t("auth.twofa.cancel", "Annuler")}</button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t("auth.login.title", "Se connecter")} subtitle={t("auth.login.subtitle", "Accédez à votre tableau de bord.")}>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label={t("auth.login.email_label", "Email")} type="email" value={email} onChange={setEmail} testid="login-email" />
        <Field label={t("auth.login.password_label", "Mot de passe")} type="password" value={password} onChange={setPassword} testid="login-password" />
        <button disabled={loading} data-testid="login-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.login.submit", "Se connecter")}
        </button>
      </form>
      <GoogleDivider />
      <GoogleButton />
      <div className="mt-4 flex items-center justify-between text-sm">
        <Link to="/register" className="text-accent hover:underline" data-testid="to-register">{t("auth.login.to_register", "Créer un compte")}</Link>
      </div>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const { t } = useContent();
  const { t: tr } = useTranslation();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [phone, setPhone] = useState("");
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!passwordValid(password)) return setFormError(tr("reg.pwd_weak"));
    if (password !== confirm) return setFormError(tr("reg.pwd_mismatch"));
    if (!/^(\+|00)\d[\d\s.-]{7,}$/.test(phone.trim())) return setFormError(tr("reg.phone_invalid"));
    setLoading(true);
    try {
      const refCode = new URLSearchParams(window.location.search).get("ref") || undefined;
      if (refCode) localStorage.setItem("twp_ref_code", refCode);
      await register(name, email, password, refCode, { password_confirm: confirm, phone: phone.trim() });
      toast.success(t("auth.register.success", "Compte créé avec succès"));
      nav("/dashboard");
    } catch (err) {
      const msg = apiError(err.response?.data?.detail) || t("auth.register.error", "Échec de l'inscription");
      setFormError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("auth.register.title", "Créer un compte")} subtitle={t("auth.register.subtitle", "Commencez gratuitement en quelques secondes.")}>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label={t("auth.register.name_label", "Nom complet")} value={name} onChange={setName} testid="register-name" />
        <Field label={t("auth.register.email_label", "Email")} type="email" value={email} onChange={setEmail} testid="register-email" />
        <div>
          <Field label={tr("reg.phone_label")} type="tel" value={phone} onChange={setPhone} testid="register-phone" />
          <p className="mt-1 text-xs text-muted-foreground">{tr("reg.phone_hint")}</p>
        </div>
        <div>
          <PasswordField label={t("auth.register.password_label", "Mot de passe")} value={password} onChange={setPassword} testid="register-password" />
          <StrengthMeter password={password} />
        </div>
        <div>
          <PasswordField label={tr("reg.password_confirm")} value={confirm} onChange={setConfirm} testid="register-password-confirm" />
          {mismatch && <p className="mt-1 text-xs text-red-500" data-testid="register-password-mismatch">{tr("reg.pwd_mismatch")}</p>}
        </div>
        {formError && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500" data-testid="register-error">{formError}</p>}
        <button disabled={loading || mismatch} data-testid="register-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.register.submit", "Créer mon compte")}
        </button>
      </form>
      <GoogleDivider />
      <GoogleButton />
      <div className="mt-4 text-sm">
        {t("auth.register.to_login_prefix", "Déjà inscrit ?")} <Link to="/login" className="text-accent hover:underline" data-testid="to-login">{t("auth.register.to_login", "Se connecter")}</Link>
      </div>
    </AuthShell>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input required type={type} value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-border bg-secondary/50 px-3 py-2.5 text-sm outline-none transition-shadow focus:ring-2 focus:ring-accent/50" />
    </div>
  );
}

function GoogleDivider() {
  const { t } = useContent();
  return (
    <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
      <span className="h-px flex-1 bg-border" /> {t("auth.or", "ou")} <span className="h-px flex-1 bg-border" />
    </div>
  );
}

const GoogleG = () => (
  <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

function GoogleButton() {
  const { t } = useContent();
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google/callback";
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      prompt: "select_account",
    });
    window.location.href = "https://accounts.google.com/o/oauth2/v2/auth?" + params.toString();
  };
  return (
    <button type="button" onClick={go} data-testid="google-login-btn"
      className="flex w-full items-center justify-center gap-2.5 rounded-full border border-border bg-card py-2.5 text-sm font-medium transition-colors hover:bg-secondary">
      <GoogleG /> {t("auth.google", "Continuer avec Google")}
    </button>
  );
}

export function GoogleAuthCallback() {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const { t } = useContent();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (!code) { nav("/login"); return; }
    (async () => {
      try {
        const redirectUri = window.location.origin + "/auth/google/callback";
        const { data } = await api.post("/auth/google", { code, redirect_uri: redirectUri });
        setSession(data.token, data.user);
        window.history.replaceState(null, "", "/dashboard");
        toast.success(t("auth.login.success", "Connexion réussie"));
        nav("/dashboard");
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail) || t("auth.google.error", "Échec de la connexion Google"));
        nav("/login");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="flex min-h-screen items-center justify-center" data-testid="google-callback">
      <Loader2 className="h-6 w-6 animate-spin text-accent" />
    </div>
  );
}
